/**
 * The metronome.
 *
 * A click is a time and whether it falls on the downbeat; what it sounds like is the
 * player's business. The times are taken from the schedule the music was built into rather
 * than worked out again from the tempo marks: a click that is derived separately drifts
 * away from the notes it is supposed to be under as soon as the piece changes speed.
 *
 * Beats are the denominator of the time signature, so 6/8 clicks six times and not twice.
 * Which beats a conductor would actually give is an interpretation; a metronome is not a
 * conductor, and a player counting a bar of 6/8 wants all six.
 *
 * A count-in sits before the music at negative times, using the first bar's beat, which is
 * why the player has to be able to stand at a negative position.
 */

import { resolveAttributes } from "../document/query";
import type { Score } from "../document/types";
import type { Schedule } from "./schedule";

/** One beat of the metronome. `start` is seconds from the beginning of the piece. */
export interface Click {
  readonly start: number;
  /** The first beat of a bar, which is usually sounded higher. */
  readonly accent: boolean;
}

export interface ClickOptions {
  /** Bars of counting before the music starts. Their clicks have negative times. */
  readonly countInBars?: number;
}

/** Where each played bar ends: the next bar's start, or the end of the piece. */
function barEnd(schedule: Schedule, at: number): number {
  return schedule.bars[at + 1]?.start ?? schedule.seconds;
}

/**
 * Every click of the piece, in order.
 *
 * A bar's beat is its own length divided by its beat count, so a bar that is played twice
 * through a repeat is clicked twice, and a bar at a new tempo clicks at the new speed
 * without this ever reading a tempo mark.
 */
export function clickTrack(
  score: Score,
  schedule: Schedule,
  options: ClickOptions = {},
): readonly Click[] {
  const clicks: Click[] = [];

  schedule.bars.forEach((bar, at) => {
    const { time } = resolveAttributes(score, bar.index);
    const beats = Math.max(1, Math.round(time.beats));
    const beat = (barEnd(schedule, at) - bar.start) / beats;
    for (let count = 0; count < beats; count += 1) {
      clicks.push({ start: bar.start + beat * count, accent: count === 0 });
    }
  });

  const bars = Math.max(0, Math.round(options.countInBars ?? 0));
  if (bars === 0 || schedule.bars.length === 0) return clicks;

  const first = schedule.bars[0];
  const { time } = resolveAttributes(score, first.index);
  const beats = Math.max(1, Math.round(time.beats));
  const beat = (barEnd(schedule, 0) - first.start) / beats;
  const counted: Click[] = [];
  for (let bar = bars; bar > 0; bar -= 1) {
    for (let count = 0; count < beats; count += 1) {
      counted.push({ start: (count - bar * beats) * beat, accent: count === 0 });
    }
  }
  return [...counted, ...clicks];
}

/** How long a count-in lasts, which is how far before zero the player has to start. */
export function countInSeconds(clicks: readonly Click[]): number {
  const first = clicks[0]?.start ?? 0;
  return first < 0 ? -first : 0;
}
